import BenchlingConfig from 'BenchlingConfig';
import {RoutineExecutionKind, RoutineRunAs} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import {TriggerType} from 'llm/routines/RoutineTypes';
import {HasAvatar} from 'ui-library/avatar/Avatar';

type ChatSessionNode = Extract<ChatSessionQuery['chatSession'], {__typename: 'ChatSessionNode'}>;
type RoutineExecutionOnSession = ChatSessionNode['routineExecution'];

type RoutineSenderFields = HasAvatar & {
  id: string;
  runAs: RoutineRunAs;
  triggerType: TriggerType;
};

export interface ChatMessageSenderContext {
  routine: RoutineSenderFields;
  executionKind: RoutineExecutionKind | null;
}

function isRoutineNode(
  routine: {__typename: string} | null | undefined
): routine is RoutineSenderFields & {__typename: 'RoutineNode'} {
  return routine?.__typename === 'RoutineNode';
}

function contextFromRoutineExecution(
  routineExecution: RoutineExecutionOnSession
): ChatMessageSenderContext | null {
  if (routineExecution == null || isInaccessible(routineExecution)) {
    return null;
  }
  if (!isRoutineNode(routineExecution.routine)) {
    return null;
  }
  return {
    routine: routineExecution.routine,
    executionKind: routineExecution.executionKind ?? null,
  };
}

/**
 * Builds message-sender context for routine-backed chats. Returns null when the run-as-service-account
 * rollout is off, when the execution is inaccessible, or when the session is not tied to a readable agent routine.
 */
export function getChatMessageSenderContext(
  chatSession: ChatSessionNode | undefined
): ChatMessageSenderContext | null {
  if (!BenchlingConfig.LLM_ENABLE_ROUTINES_RUN_AS_SERVICE_ACCOUNT || chatSession == null) {
    return null;
  }

  return contextFromRoutineExecution(chatSession.routineExecution);
}
